import * as React from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios';
import { colors, shadows } from '../styles/personalakteStyles';

type Props = {
  open: boolean;
  onClose: () => void;
  onCommitted?: (importedEmails?: string[]) => void;
};

type ParsedRow = {
  first_name: string;
  last_name: string;
  email: string;
  _lineNum: number;
};

type ValidationResult = {
  row: ParsedRow;
  valid: boolean;
  errors: string[];
};

type ImportResult = ParsedRow & {
  status: 'success' | 'error';
  error?: string;
};

type Phase = 'upload' | 'preview' | 'importing' | 'done';

const HEADER_MAP: Record<string, string> = {
  'first_name': 'first_name', 'vorname': 'first_name', 'firstname': 'first_name', 'first name': 'first_name',
  'last_name': 'last_name', 'nachname': 'last_name', 'lastname': 'last_name', 'last name': 'last_name', 'name': 'last_name',
  'email': 'email', 'e-mail': 'email', 'e_mail': 'email', 'mail': 'email',
};

let __exceljsPromise: Promise<typeof import('exceljs')> | null = null;
function getExcelJS() {
  if (!__exceljsPromise) __exceljsPromise = import('exceljs');
  return __exceljsPromise;
}

function normalizeRow(raw: Record<string, any>, lineNum: number): ParsedRow {
  const norm: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    let key = String(k).trim().toLowerCase();
    if (key.charCodeAt(0) === 0xfeff) key = key.slice(1);
    const target = HEADER_MAP[key];
    if (target) norm[target] = String(v ?? '').trim();
  }
  return {
    first_name: norm.first_name || '',
    last_name: norm.last_name || '',
    email: norm.email || '',
    _lineNum: lineNum,
  };
}

function validateRow(row: ParsedRow, allEmails: Map<string, number[]>): ValidationResult {
  const errors: string[] = [];
  if (!row.first_name) errors.push('First name missing');
  if (!row.last_name) errors.push('Last name missing');
  if (!row.email) errors.push('Email missing');
  else if (!row.email.includes('@')) errors.push('Invalid email format');

  const emailLower = row.email.toLowerCase();
  const dupeLines = allEmails.get(emailLower);
  if (dupeLines && dupeLines.length > 1) {
    errors.push(`Duplicate email (lines ${dupeLines.join(', ')})`);
  }

  return { row, valid: errors.length === 0, errors };
}

async function parseFile(file: File): Promise<ParsedRow[]> {
  const ExcelJS = await getExcelJS();
  const ext = file.name.toLowerCase();
  const wb = new ExcelJS.Workbook();

  if (ext.endsWith('.csv')) {
    const text = await file.text();
    const stream = new ReadableStream({ start(c) { c.enqueue(new TextEncoder().encode(text)); c.close(); } });
    await wb.csv.read(stream as any);
  } else {
    const buf = await file.arrayBuffer();
    await wb.xlsx.load(buf);
  }

  const ws = wb.getWorksheet(1);
  if (!ws) return [];

  const rows: any[][] = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    rows.push((row.values as any[]).slice(1));
  });
  if (rows.length === 0) return [];

  const headers = rows[0].map((h: any) => String(h ?? ''));
  const rawRows = rows.slice(1).map(row => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
    return obj;
  });

  const DEMO_EMAILS = ['max@example.com', 'anna@example.com'];
  return rawRows
    .map((r, i) => normalizeRow(r, i + 2))
    .filter(r => !DEMO_EMAILS.includes(r.email.toLowerCase()));
}

function extractErrorMessage(err: any): string {
  if (err?.response?.data) {
    const data = err.response.data;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.email) return Array.isArray(data.email) ? data.email.join(', ') : data.email;
    return JSON.stringify(data);
  }
  if (err?.data) {
    const data = err.data;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.email) return Array.isArray(data.email) ? data.email.join(', ') : data.email;
    return JSON.stringify(data);
  }
  return err?.message || 'Unknown error';
}

export default function ImportEmployeesModal({ open, onClose, onCommitted }: Props): React.JSX.Element | null {
  const [file, setFile] = React.useState<File | null>(null);
  const [rows, setRows] = React.useState<ValidationResult[]>([]);
  const [phase, setPhase] = React.useState<Phase>('upload');
  const [results, setResults] = React.useState<ImportResult[]>([]);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setRows([]);
      setPhase('upload');
      setResults([]);
      setProgress(0);
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 };
  const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)', padding: 28, width: 700, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' };
  const btnPrimary: React.CSSProperties = { background: `linear-gradient(90deg, ${colors.navy} 0%, ${colors.navy} 30%, ${colors.magenta} 100%)`, color: '#fff', border: 'none', borderRadius: 9999, padding: '10px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 8px rgba(231, 0, 125, 0.15)', transition: 'all 0.15s' };
  const btnSecondary: React.CSSProperties = { background: '#fff', color: colors.navy, border: `1px solid ${colors.border}`, borderRadius: 9999, padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.15s' };
  const sectionStyle: React.CSSProperties = { padding: 20, border: `1px solid ${colors.border}`, borderRadius: 16, background: colors.bgMuted };
  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' };

  const validCount = rows.filter(r => r.valid).length;
  const invalidCount = rows.filter(r => !r.valid).length;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setRows([]);
    setPhase('upload');
    setError('');
  }

  async function handlePreview() {
    if (!file) return;
    setError('');
    try {
      const parsed = await parseFile(file);
      if (parsed.length === 0) {
        setError('No rows found in file. Check that the headers match: first_name, last_name, email');
        return;
      }

      const emailMap = new Map<string, number[]>();
      for (const row of parsed) {
        if (row.email) {
          const key = row.email.toLowerCase();
          const existing = emailMap.get(key) || [];
          existing.push(row._lineNum);
          emailMap.set(key, existing);
        }
      }

      const validated = parsed.map(r => validateRow(r, emailMap));
      setRows(validated);
      setPhase('preview');
    } catch (err: any) {
      setError('Failed to parse file: ' + (err?.message || 'Unknown error'));
    }
  }

  async function handleImport() {
    const validRows = rows.filter(r => r.valid);
    if (validRows.length === 0) return;

    setPhase('importing');
    setProgress(0);

    const invitations = validRows.map(r => ({
      first_name: r.row.first_name,
      last_name: r.row.last_name,
      email: r.row.email,
    }));

    try {
      const res = await axiosInstance.post('/api/admin/users/invitations/bulk/', { invitations });
      const serverResults: Array<{ email: string; status: string; error?: string }> = res.data?.results || [];

      const importResults: ImportResult[] = validRows.map(r => {
        const match = serverResults.find(sr => sr.email.toLowerCase() === r.row.email.toLowerCase());
        if (match && match.status === 'error') {
          return { ...r.row, status: 'error' as const, error: match.error || 'Unknown error' };
        }
        return { ...r.row, status: 'success' as const };
      });

      setProgress(validRows.length);

      const successEmails = importResults.filter(r => r.status === 'success').map(r => r.email.toLowerCase());
      const eCount = importResults.filter(r => r.status === 'error').length;

      if (successEmails.length > 0 && onCommitted) {
        onCommitted(successEmails);
      }

      if (eCount === 0) {
        toast.success(`${successEmails.length} employee${successEmails.length !== 1 ? 's' : ''} invited successfully`);
      } else {
        toast.warning(`${successEmails.length} invited, ${eCount} failed`);
      }
      onClose();
    } catch (err: any) {
      toast.error('Import failed: ' + extractErrorMessage(err));
      onClose();
    }
  }

  async function handleDownloadTemplate() {
    try {
      const ExcelJS = await getExcelJS();
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Employees');
      ws.addRow(['first_name', 'last_name', 'email']);
      ws.addRow(['Max', 'Mustermann', 'max@example.com']);
      ws.addRow(['Anna', 'Schmidt', 'anna@example.com']);
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      const csv = 'first_name;last_name;email\nMax;Mustermann;max@example.com\nAnna;Schmidt;anna@example.com\n';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  }

  function downloadErrorsCsv() {
    const errs = results.filter(r => r.status === 'error');
    if (errs.length === 0) return;
    const headers = ['line,first_name,last_name,email,error'];
    const esc = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n;]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const csvRows = errs.map(r => [r._lineNum, r.first_name, r.last_name, r.email, r.error].map(esc).join(','));
    const csv = [...headers, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <div role="dialog" aria-modal="true" style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.navy }}>Import Employees</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: colors.bgMuted,
              border: 'none',
              fontSize: 16, cursor: 'pointer', color: colors.textMuted,
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, flexShrink: 0,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
            onMouseLeave={e => { e.currentTarget.style.background = colors.bgMuted; }}
          >{'×'}</button>
        </div>

        {/* Upload Phase */}
        {phase === 'upload' && (
          <>
            <div style={{ ...sectionStyle, marginBottom: 12 }}>
              <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>Upload a CSV or XLSX file with columns: first_name, last_name, email</div>
              <input id="emp-import-file" type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} style={{ display: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => document.querySelector<HTMLInputElement>('#emp-import-file')?.click()} style={btnSecondary}>Choose File</button>
                <span style={{ fontSize: 13, color: file ? colors.textBody : colors.textLight, flex: 1 }}>{file ? file.name : 'No file selected'}</span>
                {file && (
                  <button
                    onClick={handlePreview}
                    style={{ ...btnPrimary, opacity: 1 }}
                  >
                    Preview
                  </button>
                )}
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 12, marginTop: 8 }}>
              <button onClick={handleDownloadTemplate} style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }}>
                Download Template
              </button>
            </div>
          </>
        )}

        {/* Preview Phase */}
        {phase === 'preview' && (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12 }}>
              {validCount > 0 && <span style={{ color: colors.success, fontWeight: 500 }}>{validCount} valid</span>}
              {invalidCount > 0 && <span style={{ color: colors.danger, fontWeight: 500 }}>{invalidCount} invalid</span>}
              <span style={{ color: colors.textMuted }}>{rows.length} total rows</span>
            </div>

            <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: colors.bgMuted, position: 'sticky', top: 0 }}>
                    <th style={thStyle}>Line</th>
                    <th style={thStyle}>First Name</th>
                    <th style={thStyle}>Last Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const rowBg = r.valid ? colors.successLight : colors.dangerLight;
                    const rowColor = r.valid ? '#065f46' : colors.danger;
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}`, background: rowBg, color: rowColor }}>
                        <td style={{ padding: '6px' }}>{r.row._lineNum}</td>
                        <td style={{ padding: '6px' }}>{r.row.first_name || '—'}</td>
                        <td style={{ padding: '6px' }}>{r.row.last_name || '—'}</td>
                        <td style={{ padding: '6px' }}>{r.row.email || '—'}</td>
                        <td style={{ padding: '6px', fontSize: 12 }}>
                          {r.valid ? 'OK' : r.errors.join(', ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                style={{ ...btnPrimary, flex: 1, padding: '10px 0', opacity: validCount === 0 ? 0.5 : 1, cursor: validCount === 0 ? 'not-allowed' : 'pointer' }}
              >
                Import {validCount} employee{validCount !== 1 ? 's' : ''}
              </button>
              <button
                onClick={() => { setPhase('upload'); setRows([]); setFile(null); }}
                style={{ ...btnSecondary, flex: 1, padding: '10px 0' }}
              >
                New File
              </button>
            </div>
          </>
        )}

        {/* Importing Phase */}
        {phase === 'importing' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: colors.textBody, marginBottom: 16 }}>
              Importing {validCount} employee{validCount !== 1 ? 's' : ''}...
            </div>
            <div style={{ width: '100%', height: 8, background: colors.bgMuted, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, ${colors.navy}, ${colors.magenta})`,
                borderRadius: 4,
                animation: 'pulse 1.5s ease-in-out infinite',
                opacity: 0.7,
              }} />
            </div>
          </div>
        )}

        {/* Done Phase */}
        {phase === 'done' && (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{errorCount === 0 ? '✓' : '⚠'}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 8 }}>Import Complete</div>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 13 }}>
                {successCount > 0 && <span style={{ color: colors.success, fontWeight: 500 }}>{successCount} created</span>}
                {errorCount > 0 && <span style={{ color: colors.danger, fontWeight: 500 }}>{errorCount} failed</span>}
              </div>
            </div>

            {errorCount > 0 && (
              <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 16, marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: colors.bgMuted, position: 'sticky', top: 0 }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.filter(r => r.status === 'error').map((r, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}`, background: colors.dangerLight, color: colors.danger }}>
                        <td style={{ padding: '6px' }}>{r.first_name} {r.last_name}</td>
                        <td style={{ padding: '6px' }}>{r.email}</td>
                        <td style={{ padding: '6px', fontSize: 12 }}>{r.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              {errorCount > 0 && (
                <button onClick={downloadErrorsCsv} style={{ ...btnSecondary, flex: 1, padding: '10px 0' }}>
                  Download Errors CSV
                </button>
              )}
              <button onClick={onClose} style={{ ...btnPrimary, flex: 1, padding: '10px 0' }}>
                Close
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {error && (
          <div role="alert" style={{
            padding: '10px 14px', borderRadius: 10, marginTop: 12,
            background: colors.dangerLight, color: colors.danger,
            fontSize: 12, fontWeight: 500,
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
